import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './RfiLogTable.css';
import {
  Tooltip,
  OverlayTrigger,
  Button as ReactButton,
} from 'react-bootstrap';
import SimpleTable from 'components/shared/table/SimpleTable';
import SimpleHeader from 'components/shared/table/SimpleHeader';
import SimplePagination from 'components/shared/pagination/SimplePagination';
import Date from 'components/shared/text/Date';
import Button from 'components/shared/button/Button';
import BaseStatus from 'components/shared/text/BaseStatus';
import { TYPES, usersUtil } from 'services/utils/users-util';
import { Enterprise } from 'domain/enterprise';
import { RfiStatusEnum } from 'domain/rfi-status-enum';
import { formatNumber } from 'services/utils/text-util';

class RfiLogTable extends Component {
  static propTypes = {
    rfis: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    total: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    currentStatus: PropTypes.string.isRequired,
    pageSize: PropTypes.number.isRequired,
    currentUser: PropTypes.shape({}).isRequired,
    contribType: PropTypes.string.isRequired,
    unreadById: PropTypes.shape({}),
    rfiTabSelected: PropTypes.number.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    handleOpenRfi: PropTypes.func.isRequired,
  };

  subcontractorExists = (rfi) => {
    const { currentUser } = this.props;
    return (
      currentUser.accountable_id === rfi.contractor_id &&
      usersUtil.hasType(currentUser, TYPES.CONTRACTOR)
    );
  };

  addlAssignees = (rfi) => {
    const { currentUser } = this.props;
    return rfi.rfi_questions
      .map((qus) => qus.rfi_addl_assignees)
      .flat()
      .filter((addlAssignee) => currentUser.id === addlAssignee.assignee_id);
  };

  assigneeExists = (rfi) => {
    const { currentUser } = this.props;
    return (
      !!rfi.assignee_ids.find((id) => currentUser.id === id) ||
      this.addlAssignees(rfi).length > 0
    );
  };

  responderExists = (rfi) => {
    const { currentUser } = this.props;
    return (
      !!rfi.responder_ids.find((id) => currentUser.id === id) ||
      !!this.addlAssignees(rfi).find((addlAssignee) => !!addlAssignee.response)
    );
  };

  onlyUnique = (value, index, self) => {
    return self.indexOf(value) === index;
  };

  uniqAssigneeIds = (rfi) => {
    return rfi.rfi_questions
      .map((qus) => [
        qus.assignee_id,
        ...qus.rfi_addl_assignees.map(
          (addlAssignee) => addlAssignee.assignee_id
        ),
      ])
      .flat()
      .filter((assigneeId) => !!assigneeId)
      .filter(this.onlyUnique);
  };

  questionsForAssigneeId = (rfi, assigneeId) => {
    return rfi.rfi_questions
      .map((question) => {
        const addlAssignee = question.rfi_addl_assignees.find(
          (addlAssignee) => addlAssignee.assignee_id === assigneeId
        );
        if (question.assignee_id === assigneeId) {
          return question;
        } else if (addlAssignee) {
          return { ...question, ...addlAssignee };
        }

        return null;
      })
      .filter((question) => !!question);
  };

  responsesForQuestions = (questions) => {
    return questions
      .filter((qus) => !!qus.response)
      .map((qus, index) => (
        <p key={index}>
          {qus.number}. {qus.response}
        </p>
      ));
  };

  renderAssigneeTooltip = (label, response) => {
    return (
      <Tooltip id="tooltip-assignee-toggle">
        {label}
        {response && <div>{response}</div>}
      </Tooltip>
    );
  };

  renderAssigneeCircle = (label, response, checked, key) => {
    return (
      <OverlayTrigger
        key={key}
        placement="bottom"
        overlay={this.renderAssigneeTooltip(label, response)}
      >
        <ReactButton className="addl-assignee-circle">
          <span role="img" aria-label="circle">
            {checked ? '🟢' : '⚪'}
          </span>
        </ReactButton>
      </OverlayTrigger>
    );
  };

  handleGenerateLink = (index) => {
    const { rfis } = this.props;
    return `?rfiId=${rfis[index].id}`;
  };

  mapRfis = () => {
    const { contribType } = this.props;
    const spackleOrGc =
      Enterprise.spackle(contribType) ||
      Enterprise.generalContractor(contribType);

    return this.props.rfis.map((rfi) => {
      const rfiStatusEnum = RfiStatusEnum.create(rfi.status);
      const rfiStatusLabel = rfiStatusEnum.label;
      const draftRfi = RfiStatusEnum.DRAFT.equals(rfiStatusEnum);
      const draftOrPendingRfi =
        draftRfi || RfiStatusEnum.PENDING.equals(rfiStatusEnum);
      const resolvedRfi = RfiStatusEnum.RESOLVED.equals(rfiStatusEnum);
      const unread = this.props.unreadById && !!this.props.unreadById[rfi.id];
      const sub = this.subcontractorExists(rfi);
      const assigned = this.assigneeExists(rfi);
      const responded = this.responderExists(rfi);
      const uniqAssigneeIds = this.uniqAssigneeIds(rfi);
      return [
        <div>
          {unread && (
            <BaseStatus
              labels={[
                {
                  id: 'unread',
                  label: '',
                },
              ]}
              status="unread"
            />
          )}
          {formatNumber(rfi.number)}
        </div>,
        rfi.csi_code.division,
        rfi.title || '-',
        rfi.received_at ? (
          <Date value={rfi.received_at} format="MM/DD/YYYY" />
        ) : (
          '-'
        ),
        rfi.submitted_at ? (
          <Date value={rfi.submitted_at} format="MM/DD/YYYY" />
        ) : (
          '-'
        ),
        rfi.due_date ? (
          <Date
            value={rfi.extended_due_date ? rfi.extended_due_date : rfi.due_date}
            format="MM/DD/YYYY"
          />
        ) : (
          '-'
        ),
        rfi.resolved_at ? (
          <Date value={rfi.resolved_at} format="MM/DD/YYYY" />
        ) : (
          '-'
        ),
        <div className={draftRfi ? 'draft' : ''}>
          {uniqAssigneeIds.length === 1 ? (
            uniqAssigneeIds.map((assigneeId, index) => {
              const questions = this.questionsForAssigneeId(rfi, assigneeId);
              const responses = this.responsesForQuestions(questions);
              return (
                <OverlayTrigger
                  key={index}
                  placement="bottom"
                  overlay={this.renderAssigneeTooltip(
                    questions[0].assignee_label,
                    responses
                  )}
                >
                  <ReactButton className="addl-assignee-circle">
                    {rfiStatusLabel.charAt(0).toUpperCase()}
                    {rfiStatusLabel.slice(1)}
                  </ReactButton>
                </OverlayTrigger>
              );
            })
          ) : (
            <div>
              {rfiStatusLabel.charAt(0).toUpperCase()}
              {rfiStatusLabel.slice(1)}
            </div>
          )}
          {uniqAssigneeIds.length > 1 && (
            <React.Fragment>
              {uniqAssigneeIds.map((assigneeId, index) => {
                const questions = this.questionsForAssigneeId(rfi, assigneeId);
                const responses = this.responsesForQuestions(questions);
                return (
                  !questions.find(
                    (qus) => !qus.response || qus.draft_response
                  ) &&
                  this.renderAssigneeCircle(
                    questions[0].assignee_label,
                    responses,
                    true,
                    index
                  )
                );
              })}
              {uniqAssigneeIds.map((assigneeId, index) => {
                const questions = this.questionsForAssigneeId(rfi, assigneeId);
                return (
                  !!questions.find(
                    (qus) => !qus.response || qus.draft_response
                  ) &&
                  this.renderAssigneeCircle(
                    questions[0].assignee_label,
                    null,
                    false,
                    index
                  )
                );
              })}
            </React.Fragment>
          )}
        </div>,
        (spackleOrGc ||
          (sub && draftOrPendingRfi) ||
          (!resolvedRfi && assigned)) && (
          <Button
            className="button-blue-grey"
            label={assigned && !responded ? 'Respond' : 'Edit'}
            onClick={() => this.props.handleOpenRfi(rfi.id, false)}
          />
        ),
        (resolvedRfi || !assigned || responded) && (
          <Button
            className={sub ? 'button-blue-grey' : 'button-white'}
            label="View"
            onClick={() => this.props.handleOpenRfi(rfi.id, true)}
          />
        ),
      ];
    });
  };

  render() {
    const {
      contribType,
      currentPage,
      currentStatus,
      pageSize,
      total,
      rfiTabSelected,
      handlePageChange,
      dueDateFilter = 'all',
    } = this.props;
    return (
      <div className="rfi-log-table">
        <SimpleTable
          className="projects-table"
          headers={[
            '#',
            'CSI CODE',
            'TITLE',
            'RECEIVED',
            'SUBMITTED',
            <SimpleHeader
              label="DUE DATE"
              filter={{
                options: [
                  {
                    id: 'all',
                    label: 'All',
                  },
                  {
                    id: 'pending',
                    label: 'Pending',
                  },
                  {
                    id: 'overdue',
                    label: 'Overdue',
                  },
                  {
                    id: 'ascending',
                    label: 'Ascending',
                  },
                  {
                    id: 'descending',
                    label: 'Descending',
                  },
                ].filter((st) => !!st),
                selected: dueDateFilter,
              }}
              onFilterOptionSelect={(filter) => {
                handlePageChange(1, currentStatus, filter);
              }}
            />,
            'RESOLVED',
            rfiTabSelected === 1 ? (
              'STATUS'
            ) : (
              <SimpleHeader
                label="STATUS"
                filter={{
                  options: [
                    Enterprise.spackleOrGc(contribType)
                      ? {
                          id: 'not_pre',
                          label: 'All',
                        }
                      : {
                          id: 'all',
                          label: 'All',
                        },
                    (Enterprise.spackleOrGc(contribType) ||
                      Enterprise.subcontractor(contribType)) && {
                      id: 'draft',
                      label: 'Draft',
                    },
                    Enterprise.subcontractor(contribType) && {
                      id: 'pending',
                      label: 'Pending',
                    },
                    {
                      id: 'assigned',
                      label: 'Assigned',
                    },
                    {
                      id: 'responded',
                      label: 'Responded',
                    },
                    {
                      id: 'resolved',
                      label: 'Resolved',
                    },
                    {
                      id: 'for_record',
                      label: 'For Record Only',
                    },
                    (Enterprise.spackleOrGc(contribType) ||
                      Enterprise.subcontractor(contribType)) && {
                      id: 'void',
                      label: 'Void',
                    },
                  ].filter((st) => !!st),
                  selected: currentStatus,
                }}
                onFilterOptionSelect={(status) => {
                  handlePageChange(1, status, dueDateFilter);
                }}
              />
            ),
            '',
            '',
          ]}
          rows={this.mapRfis()}
          emptyMessage="No RFI's added yet."
          onGenerateLink={this.handleGenerateLink}
        />
        <div className="pagination-container">
          <SimplePagination
            currentPage={currentPage}
            pageSize={pageSize}
            total={total}
            onPageChange={(page) => {
              handlePageChange(page, currentStatus);
            }}
          />
        </div>
      </div>
    );
  }
}

export default connect()(RfiLogTable);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-rfi-log/RfiLogTable.js